import {
  Component,
  DestroyRef,
  DoCheck,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink, RouterOutlet } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PromptCardComponent } from '../../../../components/toasts-and-cards/prompt-card/prompt-card.component';
import { EditCampaignDetailsComponent } from '../../../../components/organization/campaigns/edit-campaign-details/edit-campaign-details.component';
import { CampaignsService } from '../../../../services/campaign/campaigns-api.service';
import { FeedbackCardComponent } from '../../../../components/toasts-and-cards/feedback-card/feedback-card.component';
import { RoutingTabComponent } from '../../../../components/ui/tabs/routing-tab/routing-tab.component';
import { StatusComponent } from '../../../../components/ui/status/status.component';
import { HasPermissionDirective } from '../../../../directives/has-permission.directive';
import { CampaignDetails, CampaignStats } from '../types/campaignDetails';
import { PermissionsService } from '../../../../services/permissions/permissions.service';
import { formatWhen } from '../../../../../utils/sms';
import { runWindow } from './campaign-sends';

type LoadState = 'loading' | 'ready' | 'error';

/**
 * Campaign details shell: the campaign's name, status and run window, its
 * actions, and the tabs (overview, messages, recipients, reports).
 */
@Component({
  selector: 'app-campaign-details',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    MatTooltipModule,
    RoutingTabComponent,
    PromptCardComponent,
    EditCampaignDetailsComponent,
    FeedbackCardComponent,
    StatusComponent,
    HasPermissionDirective,
  ],
  templateUrl: './campaign-details.component.html',
  styleUrl: './campaign-details.component.css',
})
export class CampaignDetailsComponent implements OnInit, OnDestroy, DoCheck {
  private destroyRef = inject(DestroyRef);

  campaignId!: number;
  campaign!: CampaignDetails;
  campaignStats: CampaignStats | null = null;
  route: ActivatedRoute = inject(ActivatedRoute);
  router: Router = inject(Router);
  campaignsService = inject(CampaignsService);
  permissionsService = inject(PermissionsService);
  tabs: { title: string; link: string; count?: number }[] = [];
  showEdit = false;
  showDelete = false;
  successMessage = '';

  /** Set when the details request fails; cleared by a retry or by the data arriving. */
  private loadFailed = false;
  private canViewReports = false;

  // timeout ID for cleanup
  private successTimeoutId?: ReturnType<typeof setTimeout>;

  /** The campaign on screen belongs to this route (not a previous campaign). */
  get loaded(): boolean {
    return !!this.campaign?.id && Number(this.campaign.id) === this.campaignId;
  }

  get loadState(): LoadState {
    if (this.loaded) return 'ready';
    return this.loadFailed ? 'error' : 'loading';
  }

  get name(): string {
    return String(this.campaign?.name ?? '').trim() || 'Untitled campaign';
  }

  get description(): string {
    return String(this.campaign?.description ?? '').trim();
  }

  get window(): string {
    return runWindow(this.campaign?.startDate, this.campaign?.endDate);
  }

  get created(): string {
    return this.campaign?.createdAt ? `Created ${formatWhen(this.campaign.createdAt)}` : '';
  }

  deleteCampaign() {
    this.campaignsService.deleteCampaign(this.campaignId).subscribe(() => {
      this.showDelete = false;
      this.successMessage = 'Campaign deleted. Taking you back to campaigns.';

      // Clear existing timeout before setting a new one
      if (this.successTimeoutId) {
        clearTimeout(this.successTimeoutId);
      }
      this.successTimeoutId = setTimeout(() => {
        this.successMessage = '';
        this.successTimeoutId = undefined;

        this.router.navigate(['organization/campaigns']);
      }, 3000);
    });
  }

  retry() {
    this.load();
  }

  private load() {
    this.loadFailed = false;
    // Clear the service's error first: any error text seen after this, even
    // the same words as a moment ago, belongs to this request.
    this.campaignsService.errorMsg = '';
    this.campaignsService.fetchCampaignDetails(this.campaignId); //fetch campaign data with id
  }

  ngOnInit() {
    // Re-read when the route moves to another campaign without leaving the page.
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.campaignId = Number(params.get('id')); //get campaign id
      this.campaignsService.campaignUuid = params.get('uuid')!; //get campaign uuid

      // The service keeps the last campaign viewed; clear it so this page never
      // shows another campaign's name, figures or report while loading.
      this.campaignsService.campaignDetails.next({});
      this.campaignsService.campaignStats.next({});
      this.campaignsService.campaignReports.next({
        messagesReport: [],
        recipientsReport: [],
        messagesGraphs: null,
        recipientsGraphs: null,
      });
      this.load();
      this.buildTabs();
    });

    //assign data to component campaign variable
    this.campaignsService.campaignDetails
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.campaign = data as CampaignDetails;
        if (this.loaded) this.loadFailed = false;
        this.buildTabs();
      });

    this.campaignsService.campaignStats
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.campaignStats = data as CampaignStats;
        this.buildTabs();
      });

    // Add reports tab only if user has permission to view campaign analytics
    this.permissionsService
      .hasPermission('CAMPAIGN_MANAGEMENT', 'view-campaign-analytics')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((hasPermission) => {
        this.canViewReports = hasPermission;
        this.buildTabs();
      });
  }

  /**
   * The details request reports failure only through the service's error
   * flags; notice it here so the page can say so instead of loading forever.
   */
  ngDoCheck() {
    if (
      !this.loaded &&
      !this.loadFailed &&
      !this.campaignsService.isFetching &&
      !!this.campaignsService.errorMsg
    ) {
      this.loadFailed = true;
    }
  }

  private buildTabs() {
    if (!this.campaignId) return;
    const base = `/organization/campaigns/${this.campaignId}/${this.campaignsService.campaignUuid}`;
    const messages = this.loaded && Array.isArray(this.campaign.engagementMessages)
      ? this.campaign.engagementMessages.length
      : undefined;
    const recipients = Number.isFinite(Number(this.campaignStats?.recipientsCount)) &&
      this.campaignStats?.recipientsCount !== undefined &&
      this.campaignStats?.recipientsCount !== null
      ? Number(this.campaignStats.recipientsCount)
      : undefined;

    const tabs: { title: string; link: string; count?: number }[] = [
      { title: 'Overview', link: `${base}/overview` },
      { title: 'Messages', link: `${base}/message-content`, count: messages },
      { title: 'Recipients', link: `${base}/recipients`, count: recipients },
    ];
    if (this.canViewReports) tabs.push({ title: 'Reports', link: `${base}/reports` });

    // A new array only when something changed, so the tab strip (OnPush) keeps its place.
    const same =
      tabs.length === this.tabs.length &&
      tabs.every(
        (tab, i) =>
          tab.link === this.tabs[i].link &&
          tab.count === this.tabs[i].count &&
          tab.title === this.tabs[i].title
      );
    if (!same) this.tabs = tabs;
  }

  ngOnDestroy(): void {
    // Clear timeout when component is destroyed
    if (this.successTimeoutId) {
      clearTimeout(this.successTimeoutId);
    }
  }
}
